/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { API_ORIGIN } from './apiBase';
import { apiRequest, jsonBody } from './apiClient';

// A non-OK response (404, 500, ...) still has a JSON body — usually
// `{statusCode, message}`, not the array callers expect — so this rejects
// instead of resolving with it, letting callers' .catch(() => setX([]))
// fall back cleanly rather than crashing on `notAnArray.filter is not a
// function` when that error body gets treated as real data.
async function getJson(url) {
    return apiRequest(url.replace(API_ORIGIN, ''));
}
// Unauthenticated reads of the admin-managed catalog, for the public
// marketing site. No token/auth handling needed (unlike adminApi.ts) since
// these mirror the read-only, unguarded PublicController routes.
export const fetchPublicSuppliers = () => getJson(`${API_ORIGIN}/api/suppliers`);
export const fetchPublicProducts = () => getJson(`${API_ORIGIN}/api/products`);
// Fire-and-forget visit counter for the product-detail page (see
// ProductEntity.viewCount) — callers shouldn't block or show an error over
// a missed view tick, so this deliberately has no return value to await.
export const incrementProductView = (id) => apiRequest(`/api/products/${id}/view`, { method: 'POST' }).catch(() => {});
// Same fire-and-forget visit counter, for the company-profile page (see SupplierEntity.viewCount).
export const incrementSupplierView = (id) => apiRequest(`/api/suppliers/${id}/view`, { method: 'POST' }).catch(() => {});
export const fetchPublicSchemes = () => getJson(`${API_ORIGIN}/api/schemes`);
export const fetchPublicScheme = (id) => getJson(`${API_ORIGIN}/api/schemes/${encodeURIComponent(id)}`);
export const fetchPublicNews = () => getJson(`${API_ORIGIN}/api/news`);
export const fetchPublicNewsArticle = (slug) => getJson(`${API_ORIGIN}/api/news/${encodeURIComponent(slug)}`);
export const fetchLocationSuggestions = (query) => getJson(`${API_ORIGIN}/api/locations/search?q=${encodeURIComponent(query)}`);
export const fetchPublicSectors = () => getJson(`${API_ORIGIN}/api/sectors`);
// buyerEmail is stripped server-side (PublicService.findRfqs) — never present here.
export const fetchPublicRfqs = () => getJson(`${API_ORIGIN}/api/rfqs`);
// Active packages/plans for the /pricing page, and the sanitized subset of
// Billing Settings (GST rate, which payment methods are enabled, cheque
// instructions) needed to render the checkout flow — never GSTIN/PAN/
// address, those only ever appear inside an ownership-gated invoice PDF.
export const fetchPublicPackages = () => getJson(`${API_ORIGIN}/api/packages`);
export const fetchPublicPackage = (id) => getJson(`${API_ORIGIN}/api/packages/${id}`);
export const fetchPublicBillingSettings = () => getJson(`${API_ORIGIN}/api/billing-settings/public`);
// Platform freight catalogue (Admin → Shipping & Logistics) for the Seller Hub.
export const fetchShippingConfig = () => getJson(`${API_ORIGIN}/api/shipping-config`);
// Resolves to `null` (not an error) when no admin-managed entry exists for
// the path, since that's the expected steady state for most routes. Unlike
// getJson's other callers, a "not found" response here is a real empty body
// (Nest sends no content for a `null` return, not the literal text "null"),
// which res.json() would otherwise throw on as invalid JSON.
export async function fetchPublicSeo(path) {
    const res = await fetch(`${API_ORIGIN}/api/seo?path=${encodeURIComponent(path)}`);
    if (!res.ok)
        throw new Error(`SEO lookup responded ${res.status}`);
    const text = await res.text();
    return text ? JSON.parse(text) : null;
}
// Buyer-submitted "contact this seller" request — never delivered directly;
// sits in the admin Inquiries moderation queue until approved (see
// InquiriesService on the backend). Surfaces the backend's validation
// message (e.g. "Supplier not found") on failure rather than a generic error.
export async function submitInquiry(payload) {
    return apiRequest('/api/inquiries', {
        method: 'POST',
        body: jsonBody(payload)
    });
}
// Buyer-submitted "post a requirement" request from the public homepage —
// unlike submitInquiry above, this hits PublicController's open POST
// /api/rfqs route directly (CreateRfqPublicDto: only `title` required), no
// auth or moderation queue involved, so it appears in the live Buyer
// Requirements feed (fetchPublicRfqs) as soon as it's created.
export async function submitContactMessage(payload) {
    return apiRequest('/api/contact', {
        method: 'POST',
        body: jsonBody(payload)
    });
}
export async function submitRfq(payload) {
    return apiRequest('/api/rfqs', {
        method: 'POST',
        body: jsonBody(payload)
    });
}
// Admin-managed page/section content (Site Content tab). Resolves to the
// saved JSON document, or null when the site should use its built-in defaults.
export const fetchSiteContent = (key) => getJson(`${API_ORIGIN}/api/content/${encodeURIComponent(key)}`).then((r) => r?.value ?? null);
